import React, { useEffect, useState } from "react";
import { Send, WifiOff } from "lucide-react";
import { getTelegramStatus } from "@/lib/api";

/** Kompaktes Bot-Status-Badge im Cockpit-Header.
 *  - grüner Puls-Punkt wenn `running=true`
 *  - roter Punkt + "offline" wenn nicht
 *  - Zeigt "aktiv" oder "vor 4 min" je nach letzter empfangener Nachricht.
 *  Aktualisiert sich alle 20 Sekunden.
 */
export default function BotStatusBadge() {
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState(null);

    const load = async () => {
        try {
            const s = await getTelegramStatus();
            setStatus(s);
            setErr(null);
        } catch (e) {
            setErr(e.message || "Fehler");
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, []);

    if (err) {
        return (
            <div className="inline-flex items-center gap-2 text-xs text-gray-400" data-testid="bot-status-error">
                <WifiOff size={12} /> Bot-Status nicht abrufbar
            </div>
        );
    }
    if (!status) return null;

    const running = !!status.running;
    const last = status.last_updates && status.last_updates.length > 0
        ? status.last_updates[status.last_updates.length - 1].ts
        : null;
    const relLabel = last ? relativeTime(last) : "noch keine Nachricht heute";

    return (
        <div
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white pl-2 pr-3 py-1 text-xs"
            title={`Bot ${running ? "aktiv (Polling läuft)" : "offline"}. Letzte Nachricht: ${last || "keine"}`}
            data-testid="bot-status-badge"
        >
            <span className="relative inline-flex">
                <span
                    className={
                        "inline-block h-2 w-2 rounded-full " +
                        (running ? "bg-emerald-500" : "bg-rose-500")
                    }
                />
                {running && (
                    <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 animate-ping" />
                )}
            </span>
            <Send size={12} className="text-slate-600" />
            <span className="font-medium text-slate-700">Telegram-Bot</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600" data-testid="bot-status-last">{relLabel}</span>
        </div>
    );
}

function relativeTime(iso) {
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const sec = Math.max(0, Math.floor(diffMs / 1000));
        if (sec < 10) return "gerade eben";
        if (sec < 60) return `vor ${sec} s`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `vor ${min} min`;
        const h = Math.floor(min / 60);
        if (h < 24) return `vor ${h} h`;
        const day = Math.floor(h / 24);
        return `vor ${day} d`;
    } catch {
        return "unbekannt";
    }
}
